import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, Form, Icon, Label, Message, Modal } from 'semantic-ui-react'
import MilkFixedService, { AmountMonthType, AmountSettingsType, TradingSlotSettingsType } from '~/api/services/milkFixedPrice'
import Layout from '~/components/Layout'
import { ModalTextHeader, StyledHeader } from './style'
import { useAuth } from '~/routes/components/AuthProvider'
import { RouteTypes } from '~/routes/constants'
import cloneDeep from 'lodash.clonedeep'
import TableComponent from '~/components/TableComponent'
import { TableFooterWrapper } from '~/pages/Dairy/MilkFixedPrice/style'
import dayjs from 'dayjs'
import InfoPopup from '~/components/InfoPopup'
import { CustomInput } from '~/components/CustomInput'
import { capitalizeFirstLetter, formatNumber, handleErrors } from '~/utils/helpers'
import CustomIcon from '~/components/CustomIcon'
import i18n from '~/components/i18n'
import { useForm } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import { Nullable } from '~/types'
import ConfirmModal from './ConfirmModal'
import { FixedPricePrefixType } from '~/api/services/dashboard'
import TermsModal from '~/components/TermsModal'

const OfferAmount: React.FC = () => {
  const { systemData, systemData: { amountUnit, amountVolume, amountPrice } } = useAuth()
  const { id } = useParams()
  const { t } = useTranslation()
  const [isBusy, setIsBusy] = useState(false)
  const navigate = useNavigate()

  const [offerAmounts, setOffersAmounts] = useState<AmountMonthType[]>([])
  const [acceptTermsModal, setAcceptTermsModal] = useState(false)
  const [settingsAmounts, setSettingsAmounts] = useState<Nullable<AmountSettingsType>>(null)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [amounts, setAmounts] = useState([])
  const [tradingSettings, setTradingSettings] = useState<Nullable<TradingSlotSettingsType>>(null)
  const [uniqueId, setUniqueId] = useState('')
  const [subFarmers, setSubFarmers] = useState(null)

  const priceLabel = `(${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit})`
  const { handleSubmit, control, reset, formState: { dirtyFields } } = useForm({
    mode: 'onChange'
  });
  const tableColumnHeadersSubFarmer = [
    {
      name: i18n.t('Farmers'),
      key: 'companyName'
    },
    {
      name: i18n.t('Supplier No.'),
      key: 'supplierNo'
    },
    {
      name: i18n.t('Minimum contingent (in {{amountUnit}})', { amountUnit }),
      key: 'minimumContingent',
      type: 'number-whole'
    },
    {
      name: t('Max. Trading amount (in {{amountUnit}})', { amountUnit }),
      key: 'maxAmount',
      type: 'number-whole'
    },
    {
      name: t('Your amount offer (in {{amountUnit}})', { amountUnit }),
      key: 'amount',
      type: 'input'
    }
  ]
  const defaultPreviewColumns = [
    {
      name: t('Delivery month'),
      key: 'date',
      type: 'dateRibbon',
      format: 'MMM YYYY'
    },
    {
      name: t('Fixed price {{priceLabel}}', { priceLabel }),
      key: 'price',
      info: t('Fixed price which was offered by the dairy within the trading slot.'),
      type: 'no-unit-number'
    },
    {
      name: t('Minimum contingent (in {{amountUnit}})', { amountUnit }),
      key: 'monthValue',
      type: 'min',
      info: t('The minimum contingent is predefined by the dairy. Your offered \n\
    amount must be equal or higher than the minimum contingent.')
    },
    {
      name: t('Max. Trading amount (in {{amountUnit}})', { amountUnit }),
      type: 'number-whole',
      key: 'maxAmount',
      info: t('Your maximum trading quantity is defined by your dairy. Your offered amount \n\
    must not exceed the maximum trading amount. In addition the confirmed fixed price volumes form the past \n\
    are being cosidered for the respective trading month.')
    },
    {
      name: t('Fixed price amount (in {{amountUnit}})', { amountUnit }),
      key: 'offeredAmount',
      info: t('The amount you dairy has already confirme for the repsective trading month.'),
      type: 'number-whole'
    },
    {
      name: t('Your amount offer (in {{amountUnit}})', { amountUnit }),
      key: 'amount',
      type: 'input',
      info: t('Your offered amount for the respective trading months is higher than the minimum \n\
    contingent and lower than the maximum trading quantity. Hereby you are bindingly offering \n\
    a certain amount for a fixed price which can be denied, fonfirmed or partially confirmed \n\
    by your dairy (fixed price volumes).')
    },
    {
      name: t('Revenue (in €)'),
      key: 'total'
    }
  ]
  const [offersPreviewColumns, setOffersPreviewColumns] = useState(defaultPreviewColumns)

  useEffect(() => {
    fetchOfferAmountData()
  }, [])

  useEffect(() => {
    setOffersPreviewColumns(defaultPreviewColumns)
  }, [t])

  async function fetchTradingMonthData() {
    const responseList = await MilkFixedService.farmerOfferAmountsList(id)
    if (responseList.data?.length > 0) {
      const responseSettings = await MilkFixedService.farmerOfferAmountsSettings(id)
      const responseTrading = await MilkFixedService.showTradingSlot(id)
      setUniqueId(responseTrading.data.uniqueId)
      setOffersAmounts(responseList.data)
      setSettingsAmounts(responseSettings.data)
      if (responseSettings.data?.hiddenPrices === 1) {
        setOffersPreviewColumns(offersPreviewColumns.filter(column => column.key !== 'total'))
      }
    }
  }

  async function fetchOfferAmountData() {
    setIsBusy(true)
    try {
      const responseAccepted = await MilkFixedService.checkTradingRoomsAccept(id)
      const responseSettings = await MilkFixedService.getTradingSlotsFarmerSettings()
      setTradingSettings(responseSettings.data)
      //check if terms are accepted
      if (!responseAccepted.data) {
        // check if there are terms documents
        if (responseSettings.data?.tradingRoom && responseSettings.data?.terms?.length > 0) {
          setAcceptTermsModal(true)
        } else {
          await fetchTradingMonthData()
        }
      } else {
        await fetchTradingMonthData()
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function handleChangeAllValues(
    index: number,
    value: any,
    quarterly: Nullable<number>,
    subFarmers = false
  ): void {
    const { amountPrice, amountVolume } = systemData
    const tempOfferAmount = [...offerAmounts]
    //@to-do
    // refactor model to check type of price
    //@ts-ignore
    let factorPrice = tempOfferAmount[index].price / 100
    const valueCalc = subFarmers ? value.reduce((total, amount) => parseInt(amount) + total, 0) : value
    const factorAmount = valueCalc / amountVolume
    if (amountPrice === 'e') {
      //@ts-ignore
      factorPrice = tempOfferAmount[index].price
    }
    tempOfferAmount.forEach((tempOffer) => {
      if (tempOffer.status) {
        if (!quarterly) {
          tempOffer.total = value === '' ? '' : `${formatNumber(factorPrice * factorAmount, 2)} €`
          tempOffer.amount = subFarmers ? value.reduce((total, amount) => parseInt(amount) + total, 0) : value
          if (subFarmers) {
            value.map((amount, amountIndex) => {
              tempOffer.farmers[amountIndex].amount = amount
            })
          }
        }
        else if (quarterly && tempOffer.monthValue === quarterly) {
          tempOffer.total = value === '' ? '' : `${formatNumber(factorPrice * factorAmount, 2)} €`
          tempOffer.amount = subFarmers ? value.reduce((total, amount) => parseInt(amount) + total, 0) : value
          if (subFarmers) {
            value.map((amount, amountIndex) => {
              tempOffer.farmers[amountIndex].amount = amount
            })
          }
        }
      }
    })
    setOffersAmounts(tempOfferAmount)
  }

  function handleChangeMonthValue(index, value, subFarmers = false) {
    const { amountPrice, amountVolume } = systemData
    const tempOfferAmount = [...offerAmounts]
    //@to-do
    // refactor model to check type of price
    //@ts-ignore
    let factorPrice = tempOfferAmount[index].price / 100
    const valueCalc = subFarmers ? value.reduce((total, amount) => parseInt(amount) + total, 0) : value
    const factorAmount = valueCalc / amountVolume
    if (amountPrice === 'e') {
      //@ts-ignore
      factorPrice = tempOfferAmount[index].price
    }
    tempOfferAmount[index].total = value === '' ? '' : `${formatNumber(factorPrice * factorAmount, 2)} €`
    if (subFarmers) {
      value.map((amount, amountIndex) => {
        tempOfferAmount[index].farmers[amountIndex].amount = amount
      })
    }
    tempOfferAmount[index].amount = valueCalc
    setOffersAmounts(tempOfferAmount)
  }

  function handleInputChange(value, index, row, subFarmers = false) {
    const { amountOfferingType } = settingsAmounts
    switch (amountOfferingType) {
      case 'all':
        return handleChangeAllValues(index, value, null, subFarmers)
      case 'monthly':
        return handleChangeMonthValue(index, value, subFarmers)
      case 'quarterly':
        return handleChangeAllValues(index, value, row.monthValue, subFarmers)
      default:
        return
    }
  }

  async function onSubmit() {
    let amounts = []
    if (settingsAmounts?.individualSubSupplierOffer) {
      offerAmounts.map(offer => {
        let farmerAmountsPayload = []
        if (offer.farmers) {
          farmerAmountsPayload = offer?.farmers?.map((offerFarmer) => offerFarmer && offerFarmer.amount !== '' && ({
            id: offer.id,
            amount: parseInt(offerFarmer.amount),
            farmerId: offerFarmer.id
          })).filter(amount => !!amount.amount)
        } else {
          farmerAmountsPayload = offer && offer.amount !== '' ? [{
            id: offer.id,
            amount: parseInt(offer.amount),
            farmerId: settingsAmounts?.farmerId
          }].filter(amount => !!amount.amount) : []
        }
        amounts = [...amounts, ...farmerAmountsPayload]
      })
    } else {
      amounts = offerAmounts.map((offer) => offer && offer.amount !== '' && ({
        id: offer.id,
        amount: parseInt(offer.amount)
      })).filter(amount => !!amount.amount)
    }
    setAmounts(amounts)
    setOpenConfirmModal(true)
  }

  function getMinimumVerified(offerAmount) {
    const disableBelowMinimum = Boolean(settingsAmounts?.minContingentOffer)
    return disableBelowMinimum ? offerAmount.amount < parseInt(settingsAmounts?.minimumContingent) : false
  }

  function getMaximumVerified(offerAmount) {
    const exceedingMaxAllowed = Boolean(settingsAmounts?.exceedingMaxAllowed)
    return exceedingMaxAllowed ? false : offerAmount.amount > offerAmount.maxAmount
  }

  function errorFields() {
    let errorClass = false
    offerAmounts.map(offerAmount => {
      const offerAmountEmpty = !offerAmount.amount || offerAmount.amount === ''
      if (!offerAmountEmpty && (getMinimumVerified(offerAmount) || getMaximumVerified(offerAmount))) {
        errorClass = true
        return
      }
    })
    return errorClass
  }

  function renderTableFooter() {
    const amounts = offerAmounts ? offerAmounts.filter(offerAmount => !!offerAmount?.amount) : []
    return <TableFooterWrapper>
      <div>
        <Label basic pointing={'left'}>
          {t('Total: ')}
          <Label.Detail>{offerAmounts.length}</Label.Detail>
        </Label>
      </div>
      <Button
        disabled={(amounts.length === 0) || errorFields()}
        size='small'
        onClick={() => onSubmit()}
        primary
      >{t('Offer amount')}
      </Button>
    </TableFooterWrapper>
  }

  function getContent(deliveryDate, cancelationDate = false) {
    const deliveryDateFormated = dayjs(deliveryDate).format('DD.MM.YYYY')
    const message = !cancelationDate ?
      t('Unfortunately, you cannot offer amounts for this month and all subsequent months, as \n\
    this is after your last delivery date : {{deliveryDateFormated}}. Hedging transactions are only (still) possible for the preceding months.', { deliveryDateFormated }) :
      t('Unfortunately, you cannot offer amounts for this month and all \n\
    subsequent months, as this is after your cancellation date: {{deliveryDateFormated}}.', { deliveryDateFormated })
    return <div>
      {message}
    </div>
  }

  function renderOfferInputs(type, value, row, index) {
    const disableBelowMinimum = Boolean(settingsAmounts?.minContingentOffer)
    const exceedingMaxAllowed = Boolean(settingsAmounts?.exceedingMaxAllowed)
    const enableIndiOffers = Boolean(settingsAmounts?.individualSubSupplierOffer) && row?.farmers?.length > 0
    const enableSubFarmerOffers = enableIndiOffers && row?.farmers?.length > 0
    return <div key={row.id + 'inpput'} style={{ display: 'flex', alignItems: 'baseline' }}>
      <CustomInput
        value={value}
        id={row.id + 'input'}
        onChange={(value) => handleInputChange(value || '', index, row)}
        allowDecimals={false}
        disableOpacity={enableSubFarmerOffers}
        disabled={!row.status || enableSubFarmerOffers}
        placeholder={capitalizeFirstLetter(dayjs(row.date).format('MMM YYYY'))}
        error={!value || value === '' ? false : (disableBelowMinimum ? value < parseInt(settingsAmounts?.minimumContingent) : false) ||
          (exceedingMaxAllowed ? false : value > row.maxAmount)}
      />
      {!enableIndiOffers && !row.status && row.deliveryDate && <InfoPopup
        content={getContent(row.deliveryDate)}
        styleIcon={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 10 }}
      />
      }
      {!enableIndiOffers && !row.status && !row.deliveryDate && row.terminationDate && <InfoPopup
        content={getContent(row.terminationDate, true)}
        styleIcon={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 10 }}
      />
      }
      {!enableIndiOffers && !row.status && !row.deliveryDate && !row.terminationDate && <InfoPopup
        content={t('Not available.')}
        styleIcon={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 10 }}
      />
      }
      {enableSubFarmerOffers && <CustomIcon
        name={'reply all'}
        disabled={Boolean(!row.status)}
        onClick={() => {
          if (row.status) {
            setSubFarmers({
              farmers: row.farmers,
              month: capitalizeFirstLetter(dayjs(row.data).format('MMM YYYY')),
              index: index
            })
            reset({ amounts: row.farmers.map(farmer => farmer?.amount || '') })
          }
        }
        }
        style={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 10 }}
      />
      }
    </div>
  }

  function renderContent() {
    return <div>
      <TableComponent
        tableColumnHeaders={offersPreviewColumns}
        handleCellValueChange={(value, index, row) => handleInputChange(value, index, row)}
        rows={offerAmounts}
        renderColumn={(type, value, row, index) => renderOfferInputs(type, value, row, index)}
        options={{
          minimumContingent: settingsAmounts.minimumContingent
        }}
        removePagination
        infoNextToText
        customTableFooter={renderTableFooter()}
        total={offerAmounts.length}
      />
    </div>
  }

  function renderConfirmModal() {
    const rowsWithOfferedAmount = offerAmounts.filter(offerAmount => !!offerAmount.total && offerAmount.total !== '0 €')
    const columns = cloneDeep(offersPreviewColumns)
    columns[5].type = 'number-whole'
    columns[5].key = 'amount'

    return <ConfirmModal
      tradingSettings={tradingSettings}
      amounts={amounts}
      offerAmounts={rowsWithOfferedAmount}
      offersPreviewColumns={columns}
      handleClose={(submited?) => {
        setOpenConfirmModal(false)
        setAmounts([])
        if(submited) {
          navigate(RouteTypes.MILK_FIXED_PRICE)
        }
      }}
      openConfirmModal={openConfirmModal}
      settingsAmounts={settingsAmounts}
    />
  }

  function renderOfferSubFarmer(type, value, row, index) {
    const disableInput = Boolean(row.deliveryDate || row.terminationDate)
    return <Form.Group key={row.id} style={{ alignItems: 'center' }}>
      <FormFields.Input
        control={control}
        typeInput={'controled'}
        withoutAsterisk
        disabled={disableInput}
        name={`amounts[${index}]`}
        allowDecimals={false}
        placeholder={capitalizeFirstLetter(dayjs(row.date).format('MMM YYYY'))}
        rules={{
          min: settingsAmounts?.minContingentOffer ? row?.minimumContingent : 0,
          max: settingsAmounts?.exceedingMaxAllowed ? null : row?.maxAmount
        }}
        noErrorMessage
      />
      <Form.Field>
        {row.deliveryDate && <InfoPopup
          content={getContent(row.deliveryDate)}
          styleIcon={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 10 }}
        />
        }
        {!row.deliveryDate && row.terminationDate && <InfoPopup
          content={getContent(row.terminationDate, true)}
          styleIcon={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 10 }}
        />
        }
      </Form.Field>
    </Form.Group>
  }

  function onSubmitSubFarmers(data) {
    data.amounts = data?.amounts.map(amount => !amount || amount === '' ? 0 : amount)
    handleInputChange(data?.amounts, subFarmers.index, offerAmounts[subFarmers.index], true)
    setSubFarmers(null)
    reset([])
  }

  function renderSubFarmersModal() {
    return <Modal
      as={Form}
      open={Boolean(subFarmers)}
      onClose={() => {
        setSubFarmers(null)
        reset([])
      }}
      onSubmit={handleSubmit(onSubmitSubFarmers)}
    >
      <Modal.Header style={{ display: 'flex', justifyContent: 'space-between' }}>
        <ModalTextHeader>{t('Offer amounts {{month}}', { month: subFarmers?.date ? `- ${subFarmers?.date}` : '' })}</ModalTextHeader>
        <Icon style={{ cursor: 'pointer' }} onClick={() => {
          setSubFarmers(null)
          reset([])
        }} name='close' size='large' />
      </Modal.Header>
      <Modal.Content>
        <TableComponent
          tableColumnHeaders={tableColumnHeadersSubFarmer}
          rows={subFarmers?.farmers}
          removePagination
          total={subFarmers?.farmers.length}
          renderColumn={(type, value, row, index) => renderOfferSubFarmer(type, value, row, index)}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button positive disabled={Object.keys(dirtyFields).length === 0}>{t('Confirm')}</Button>
        <Button negative onClick={() => {
          setSubFarmers(null)
          reset([])
        }} type={'button'} positive>{t('Abort')}</Button>
      </Modal.Actions>
    </Modal>
  }

  const dataLoaded = offerAmounts.length > 0 && settingsAmounts && !isBusy

  function renderNoTradingMonths() {
    return !isBusy && <Message info style={{ margin: '20px auto', textAlign: 'center', width: '60%' }}>
      <Message.Header>{t('No results')}</Message.Header>
      <p>{t('Sorry, there are no trading months to display.')}</p>
    </Message>
  }

  function renderHeader() {
    return <>
      <StyledHeader>{t('Offer amounts')}</StyledHeader>
      <Message info>
        <Message.Content style={{ display: 'flex', alignItems: 'baseline', fontSize: '1.1em' }}>
          <Icon name='info circle' />  <p>{t('Enter your desired amounts in the highlighted column. Please note that your offer must be between the minimum contingent and your personal, open, maximum hedge amount. After successful entry click on "Offer amounts" and follow the further steps.')}</p>
        </Message.Content>
      </Message>
    </>
  }

  async function getMonths() {
    setIsBusy(true)
    try {
      await fetchTradingMonthData()
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderTermsModal() {
    return <TermsModal
      openTermsModal={acceptTermsModal}
      prefix={FixedPricePrefixType.FixedPrice}
      handleClose={() => {
        setAcceptTermsModal(false)
        getMonths()
      }}
      hideCloseIcon
      tradingSlotId={id}
      settingsData={tradingSettings}
    />
  }

  return <Layout
    isBusy={isBusy}
    segmented
    title={t('Trading slot ID: {{uniqueId}}', { uniqueId })}
    close={RouteTypes.MILK_FIXED_PRICE}
  >
    {renderTermsModal()}
    {renderHeader()}
    {subFarmers && renderSubFarmersModal()}
    {dataLoaded ? renderContent() : renderNoTradingMonths()}
    {openConfirmModal && renderConfirmModal()}
  </Layout>
}
export default OfferAmount
